/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useUrlGenerator, useRoutePush } from '@micromag/core/contexts';
import { Button } from '@micromag/core/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import Menu from './Menu';
import { useOrganisation } from '../../contexts/OrganisationContext';
import StoryDeleteModal from '../modals/StoryDelete';
import StoryDuplicateModal from '../modals/StoryDuplicate';

const propTypes = {
    story: MicromagPropTypes.story.isRequired,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    withoutDropdown: PropTypes.bool,
    withoutBackToScreens: PropTypes.bool,
    asDropdown: PropTypes.bool,
    asList: PropTypes.bool,
    asLine: PropTypes.bool,
    flush: PropTypes.bool,
    dropdownAlign: MicromagPropTypes.dropdownAlign,
    withEditor: PropTypes.bool,
    withDuplicate: PropTypes.bool,
    withDelete: PropTypes.bool,
};

const defaultProps = {
    className: null,
    itemClassName: null,
    linkClassName: null,
    withoutDropdown: false,
    withoutBackToScreens: false,
    asList: false,
    asLine: false,
    asDropdown: false,
    flush: false,
    dropdownAlign: 'end',
    withEditor: false,
    withDuplicate: false,
    withDelete: false,
};

const StoryMenu = ({
    story,
    className,
    itemClassName,
    linkClassName,
    withoutDropdown,
    withoutBackToScreens,
    asList,
    asLine,
    asDropdown,
    flush,
    dropdownAlign,
    withEditor,
    withDuplicate,
    withDelete,
    ...props
}) => {
    const url = useUrlGenerator();
    const [pathname, setLocation] = useLocation();
    const push = useRoutePush();
    const listItemClassName = asList ? '' : null;

    const organisation = useOrganisation();
    const { permissions: organisationPermissions = null } = organisation || {};
    const {
        can_edit_story: organisationCanEditStory = false,
        can_publish_story: organisationCanPublishStory = false,
        can_edit_medias: organisationCanEditMedias = false,
        // can_read_stats: canReadStats = false,
    } = organisationPermissions || {};
    const canEditStory = organisation === null || organisationCanEditStory;
    const canPublishStory = organisation === null || organisationCanPublishStory;
    const canEditMedias = organisation === null || organisationCanEditMedias;
    const canViewParticipation = organisation === null || organisationCanEditMedias;
    // const canViewStats = organisation !== null && canReadStats;

    // Modals

    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [duplicateModalOpened, setDuplicateModalOpened] = useState(false);

    const onDeleteStoryOpenModal = useCallback(
        (e) => {
            e.preventDefault();
            setDeleteModalOpened(true);
            setDuplicateModalOpened(false);
        },
        [setDeleteModalOpened, setDuplicateModalOpened],
    );

    const onDeleteStoryComplete = useCallback(() => {
        setDeleteModalOpened(false);
        setDuplicateModalOpened(false);
        setLocation(`${pathname}?page=1&last=${Math.random()}`);
    }, [setLocation, pathname, setDeleteModalOpened]);

    const onDeleteStoryCancelled = useCallback(() => {
        setDeleteModalOpened(false);
        setDuplicateModalOpened(false);
    }, [setDeleteModalOpened]);

    const onDuplicateStoryOpenModal = useCallback(
        (e) => {
            e.preventDefault();
            setDuplicateModalOpened(true);
            setDeleteModalOpened(false);
        },
        [setDuplicateModalOpened, setDeleteModalOpened],
    );

    const onDuplicateStoryAction = useCallback(
        (e) => {
            setDuplicateModalOpened(false);
            setDeleteModalOpened(false);
            if (e && e.id) {
                push('stories.show', { story: e.id });
            }
        },
        [push, setDuplicateModalOpened, setDeleteModalOpened],
    );

    const finalItems = useMemo(() => {
        const subMenu = [
            withEditor && canEditStory
                ? {
                      id: 'editor',
                      href: url('stories.editor', {
                          story: story.id,
                      }),
                      label: <FormattedMessage defaultMessage="Editor" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            canEditStory
                ? {
                      id: 'settings',
                      href: url('stories.settings', {
                          story: story.id,
                      }),
                      label: <FormattedMessage defaultMessage="Settings" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            canPublishStory
                ? {
                      id: 'publish',
                      href: url('stories.publish', {
                          story: story.id,
                      }),
                      label: <FormattedMessage defaultMessage="Publish" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            {
                id: 'versions',
                href: url('stories.versions.index', {
                    story: story.id,
                }),
                label: <FormattedMessage defaultMessage="Versions" description="Menu label" />,
                className: listItemClassName,
            },
            canViewParticipation
                ? {
                      id: 'participation',
                      href: url('stories.participation', {
                          story: story.id,
                      }),
                      label: (
                          <FormattedMessage
                              defaultMessage="Participation"
                              description="Menu label"
                          />
                      ),
                      className: listItemClassName,
                  }
                : null,
            // canViewStats
            //     ? {
            //           id: 'stats',
            //           href: url('organisations.stats.story', {
            //               story: story.id,
            //           }),
            //           label: <FormattedMessage defaultMessage="Stats" description="Menu label" />,
            //           className: listItemClassName,
            //       }
            //     : null,
            canEditMedias
                ? {
                      id: 'medias',
                      href: url('stories.medias', {
                          story: story.id,
                      }),
                      label: <FormattedMessage defaultMessage="Media" description="Menu label" />,
                      className: listItemClassName,
                  }
                : null,
            withDuplicate && canEditStory
                ? {
                      id: 'duplicate',
                      href: null,
                      onClick: onDuplicateStoryOpenModal,
                      label: (
                          <FormattedMessage defaultMessage="Duplicate" description="Menu label" />
                      ),
                      className: asList ? listItemClassName : null,
                  }
                : null,
            withDelete && canEditStory
                ? {
                      id: 'delete',
                      href: null,
                      onClick: onDeleteStoryOpenModal,
                      label: <FormattedMessage defaultMessage="Delete" description="Menu label" />,
                      className: asList ? `${listItemClassName} list-group-item-danger` : null,
                  }
                : null,
        ]
            .filter((it) => it !== null)
            .map((it) =>
                it.href === pathname
                    ? {
                          ...it,
                          active: true,
                      }
                    : it,
            );
        return withoutDropdown || asList || asLine
            ? subMenu
            : [
                  {
                      id: 'story',
                      href: url('stories.show', {
                          story: story.id,
                      }),
                      label: story.title,
                      dropdown: subMenu,
                  },
              ];
    }, [story, url, withoutDropdown, withDelete, asList, asLine]);
    return (
        <>
            {!withoutBackToScreens ? (
                <div className="list-group mb-3">
                    <Button
                        href={url('stories.show', { story: story.id })}
                        className="py-2 px-2 py-lg-3 px-lg-3 color-light list-group-item list-group-item-action text-left"
                    >
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faChevronLeft}
                            color="currentColor"
                        />
                        <FormattedMessage defaultMessage="Screens" description="Menu label" />
                    </Button>
                </div>
            ) : null}
            <Menu
                {...props}
                items={finalItems}
                asList={asList}
                asLine={asLine}
                asDropdown={asDropdown}
                flush={flush}
                dropdownAlign={dropdownAlign}
                className={className}
                itemClassName={itemClassName}
                linkClassName={linkClassName}
            />
            {deleteModalOpened && canEditStory ? (
                <StoryDeleteModal
                    story={story}
                    onConfirm={onDeleteStoryComplete}
                    onCancel={onDeleteStoryCancelled}
                />
            ) : null}
            {duplicateModalOpened && canEditStory ? (
                <StoryDuplicateModal
                    story={story}
                    onConfirm={onDuplicateStoryAction}
                    onCancel={onDuplicateStoryAction}
                />
            ) : null}
        </>
    );
};

StoryMenu.propTypes = propTypes;
StoryMenu.defaultProps = defaultProps;

export default StoryMenu;
